import { Navigate } from "react-router-dom";
import { useAuthStore, useActiveTenant } from "@/stores/auth-store";
import { DashboardEntry } from "@/features/dashboard/layout/DashboardEntry";
import { PlatformDashboardPage } from "@/features/platform/PlatformDashboardPage";
import { EssDashboardPage } from "@/features/ess/EssDashboardPage";
import { PayrollDashboardPage } from "@/features/payroll/PayrollDashboardPage";

/**
 * The "/" index route. Four distinct cases, per SUPER_ADMIN =
 * PLATFORM_SCOPE_ONLY (docs/02-authentication-flow.md):
 *
 * - A platform Super Admin with no workspace open has no tenant to render a
 *   dashboard for (every dashboard widget is tenant-scoped and would 400
 *   TENANT_REQUIRED) - they get PlatformDashboardPage instead, the
 *   platform-level KPI view "Dashboard" in their own sidebar refers to.
 * - A Super Admin who HAS opened a workspace (`impersonatedTenant` set)
 *   must still never see the tenant's own operational DashboardPage - that
 *   would leak employee/attendance/payroll widgets through the one nav item
 *   (`alwaysVisible`, see nav-config.ts) that has no `hiddenFromSuperAdmin`
 *   or `excludeSuperAdmin` guard to catch it. Redirect into the workspace's
 *   own landing page instead.
 * - A plain Employee gets EssDashboardPage - the same "My Dashboard" page
 *   the (now-excluded-for-them) Employee Self Service section used to link
 *   to separately, promoted to be "Home" itself rather than duplicated.
 *   DashboardPage's KPIs/analytics are built for a management/admin
 *   audience (tenant headcount, payroll totals, department breakdowns) -
 *   exactly what the Employee sidebar spec says Home must never show - and
 *   EssDashboardPage already reads exclusively from self-service endpoints
 *   (attendance/leave/payroll/expenses/announcements/holidays), so this is a
 *   routing choice, not new widget code.
 * - Payroll Admin gets PayrollDashboardPage - the Payroll Admin Sidebar spec
 *   explicitly forbids a generic application-level Dashboard for this role
 *   (nav-config.ts's Dashboard item now carries excludeRoles: ["payroll_admin"]
 *   to match), so "/" must not fall through to the generic DashboardPage for
 *   them either, same reasoning as the Employee case above.
 * - Every other role gets the real tenant DashboardPage, unchanged.
 */
export function HomeRoute() {
  const user = useAuthStore((s) => s.user);
  const activeTenant = useActiveTenant();

  if (user?.is_super_admin) {
    return activeTenant ? <Navigate to="/platform/workspace" replace /> : <PlatformDashboardPage />;
  }
  if (user?.role === "employee" || user?.role === "telecaller") {
    return <EssDashboardPage />;
  }
  if (user?.role === "payroll_admin") {
    return <PayrollDashboardPage />;
  }
  return <DashboardEntry />;
}
